/**
 * Contains definition for MeasurementDataClient interface.
 */
import { of } from "rxjs";
import { tap, exhaustMap, catchError, finalize, scan, distinct, auditTime, } from "rxjs/operators";
import { createAsyncSubject } from "../AsyncSubject";
import { debug } from "./debug";
/**
 * Partially implemented class. Should be extended by implementation. This class will already implement the observables.
 * Only fetchMeasurementData and fetchMetadata should be implemented according to spec.
 */
export class AbstractMeasurementDataClient {
    constructor() {
        this.createMeasurementDataObservable = (args) => {
            const { time$, file, measurements, rows, limit, unit } = args;
            const asyncSubject = createAsyncSubject();
            const sub = time$
                .pipe(auditTime(200), tap(debug("time")), tap(() => {
                asyncSubject.loadingStart();
            }), 
            /*
             * exhaustMap() will only emit when previous projected observable has completed. This makes sure we arn't sending out an excessive amount of requests to the endpoint.
             */
            exhaustMap((offset) => this.fetchMeasurementData({
                file,
                measurements,
                offset,
                unit: unit || "MILLI",
                rows,
                limit,
            }).then((measurementData) => ({
                measurementData,
                range: { from: offset, length: limit || 0 },
            }))), catchError((err) => {
                return of(new Error("Something went wrong: " + err));
            }), tap((data) => {
                if (data instanceof Error) {
                    asyncSubject.loadingError(data);
                    return;
                }
                asyncSubject.loadingSuccess(data);
            }))
                .subscribe();
            return asyncSubject.state.pipe(finalize(() => {
                sub.unsubscribe();
            }));
        };
        this.createWindowedMeasurementDataObservable = (args) => {
            const { time$, file, measurements, visibleWindow, unit, rows } = args;
            const asyncSubject = createAsyncSubject();
            const sub = time$
                .pipe(
            // We add a slight debounce, so that if there's a lot of requests after another, we only pick the latest.
            auditTime(5), scan((acc, number) => {
                // We fetch 4 times the visible amount
                const total = visibleWindow * 4;
                // 1/4 of the total amount of time.
                const leftTreshold = acc.offset + visibleWindow;
                // 3/4 of the total amount of time.
                const rightTreshold = acc.offset + visibleWindow * 3;
                //If we pass 1/4 of the total amount of time we should get new data.
                const passedLeftTreshold = number < leftTreshold;
                // If we pass 3/4 of the total amount time also get new data.
                const passedRightTreshold = number > rightTreshold;
                return acc.offset === Infinity ||
                    passedLeftTreshold ||
                    passedRightTreshold
                    ? {
                        offset: number - total / 2,
                        limit: total,
                        rows: rows || 0 * 2,
                    }
                    : acc;
            }, { offset: Infinity, limit: 0, rows: 0 }), distinct(), tap(() => {
                asyncSubject.loadingStart();
            }), exhaustMap(({ offset, limit, rows: _rows }) => this.fetchMeasurementData({
                file,
                measurements,
                offset,
                limit,
                unit: unit || "MILLI",
                rows: _rows,
            }).then((measurementData) => ({
                measurementData,
                range: { from: offset, length: limit },
            }))), catchError((err) => {
                return of(new Error("Something went wrong: " + err));
            }), tap((data) => {
                if (data instanceof Error) {
                    asyncSubject.loadingError(data);
                    return;
                }
                asyncSubject.loadingSuccess(data);
            }))
                .subscribe();
            return asyncSubject.state.pipe(finalize(() => sub.unsubscribe()));
        };
    }
}
//# sourceMappingURL=MeasurementDataClient.js.map